import { ComponentProps, useMemo, forwardRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { format, formatISO } from "date-fns";
import { formatDate, parseISOString, timeToString } from "helper/date-time";

import { Button } from "../ui/button";
import { Calendar } from "react-date-range";
import { CalendarIcon } from "lucide-react";
import { TimeInput } from "./time-input";
import { cn } from "@/lib/utils";
import { fr as languageFr } from "date-fns/locale";

export interface DateRangeInputProps extends ComponentProps<"button"> {
  selectedDate?: Date | string | null;
  onChangeValue: (newDate: Date) => void;
  placeholder?: string;
  className?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
  showTime?: boolean;
}

export const CalendarSelector = forwardRef<HTMLDivElement, DateRangeInputProps>(
  (
    {
      selectedDate,
      minDate,
      maxDate,
      onChangeValue,
      placeholder = "Select date",
      className,
      showTime = false,
      ...buttonProps
    },
    ref,
  ) => {
    const handleChangeDate = (input: Date) => {
      const dateInput = formatISO(input, { representation: "date" });
      const timeInput = selectedDate ? format(new Date(selectedDate), "HH:mm") : "00:00";
      const newDateTime = new Date(`${dateInput}T${timeInput}`);
      onChangeValue(newDateTime);
    };

    const convertDate = (input: Date | string | undefined) => {
      if (!input) {
        return undefined;
      }
      if (typeof input === "string") {
        return new Date(input);
      }
      return input;
    };

    const newDate = useMemo((): Date => {
      if (selectedDate && typeof selectedDate === "string") {
        return parseISOString(selectedDate);
      }
      if (typeof selectedDate === "object") {
        return selectedDate as Date;
      }

      return new Date();
    }, [selectedDate]);

    const times = useMemo(() => {
      if (!newDate) {
        return "";
      }
      const hours = newDate.getHours();
      const minutes = newDate.getMinutes();
      return timeToString(hours, minutes);
    }, [newDate]);

    const changeTime = (value: string) => {
      if (!selectedDate) {
        return;
      }
      const newDate = new Date(selectedDate);
      if (value === "null") {
        newDate.setHours(0, 0, 0, 0);
        onChangeValue(newDate);
      } else {
        const dateInput = formatISO(new Date(selectedDate), { representation: "date" });
        if (!isNaN(newDate.getTime())) {
          onChangeValue(new Date(`${dateInput}T${value}:00`));
        }
      }
    };

    return (
      <div ref={ref} className="flex">
        <Popover modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn("pl-3 text-left font-normal", !selectedDate && "text-muted-foreground", className)}
              type="button"
              {...buttonProps}
            >
              {selectedDate ? (
                <span className="flex-1 truncate text-left text-medium capitalize">{formatDate(selectedDate)}</span>
              ) : (
                <span className="text-lightgrey">{placeholder}</span>
              )}
              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96">
            <div>
              <Calendar
                onChange={(item: Date) => {
                  handleChangeDate(item);
                }}
                locale={languageFr}
                date={newDate}
                minDate={convertDate(minDate)}
                maxDate={convertDate(maxDate)}
              />
            </div>
          </PopoverContent>
        </Popover>
        {showTime && (
          <TimeInput timeInputClassName="p-2" times={times} onChangeValue={changeTime} disabled={!newDate} />
        )}
      </div>
    );
  },
);

CalendarSelector.displayName = "CalendarSelector";
